import { CONFIG_ENABLE_EDD } from '@src/config/env'
import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { getProductCardEDDDetails } from '@src/quince-site-common/src/services/eddService/eddService'
import {
  EDDProductType,
  EDDRequestProducts,
  EDDRequestProductType,
} from '@src/quince-site-common/src/services/eddService/eddService.types'
import productService from '@src/quince-site-common/src/services/productService/ProductService'
import { createBatches, filterFulfilled } from '@src/utils/general.utils'

import DeliveryDetailsByColorType from '../../../domains/ProductDeliveryDetails'
import PubSub from '../../PubSub'

interface ProductDynamicDataState {
  tagsVSProductId: Record<string, ProductTag[]>
  deliveryInfoVsProductId: Record<string, DeliveryDetailsByColorType | undefined>
  newColorOrderVsProductId: Record<string, ProductOptionValue[]>
  colorOrderVsProductId: Record<string, ProductOptionValue[]>
  isWhiteGloveProductVsProductId: Record<string, boolean>
  productEDDDetails: Record<string, EDDProductType>
}

class ProductDynamicDataStateManager extends PubSub {
  private state: ProductDynamicDataState

  private productsQueueForDynamicTagFetch: (string | number)[] = []
  private eddProductsQueue: EDDRequestProducts = []
  private scheduledJob = 0
  private scheduledEDDJob = 0
  private zipCode: string | undefined

  private eddObserver: IntersectionObserver | null = null
  private eddObserverDataMap = new Map<
    Element,
    { eddRequestedProducts: EDDRequestProductType; skipExistCheck?: boolean }
  >()

  constructor() {
    super()
    this.state = {
      tagsVSProductId: {},
      deliveryInfoVsProductId: {},
      newColorOrderVsProductId: {},
      colorOrderVsProductId: {},
      isWhiteGloveProductVsProductId: {},
      productEDDDetails: {},
    }
    this.initialiseEDDIntersectionObserver()
  }

  initialiseEDDIntersectionObserver = () => {
    this.eddObserver =
      typeof window !== 'undefined' && window.IntersectionObserver
        ? new IntersectionObserver(
            entries => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  const data = this.eddObserverDataMap.get(entry.target)
                  if (data) {
                    const { eddRequestedProducts, skipExistCheck } = data
                    this.eddProductsQueue.push(eddRequestedProducts)
                    this.scheduleDynamicEDDDataFetch(skipExistCheck)
                  }
                }
              })
            },
            {
              // eslint-disable-next-line i18next/no-literal-string
              rootMargin: '1200px 0px 1200px 0px',
              threshold: 0,
            },
          )
        : null
    this.eddObserverDataMap.forEach((_, key) => {
      this.eddObserver?.observe(key)
    })
    this.state.productEDDDetails = {}
  }

  scheduleDynamicEDDDataFetch = (skipExistCheck?: boolean) => {
    if (!CONFIG_ENABLE_EDD) return
    const MAX_QUEUE_SIZE = 10

    const task = async () => {
      clearTimeout(this.scheduledEDDJob)
      const products = this.eddProductsQueue.filter(
        item => skipExistCheck || !this.state.productEDDDetails[item.product_id],
      )
      this.eddProductsQueue = []
      if (products.length && this.zipCode) {
        const eddResponse = await getProductCardEDDDetails(this.zipCode, products)
        this.state.productEDDDetails = {
          ...this.state.productEDDDetails,
          ...eddResponse.product_variants,
        }
        this.publish()
      }
    }
    clearTimeout(this.scheduledEDDJob)

    if (this.eddProductsQueue.length >= MAX_QUEUE_SIZE) {
      task().catch((e: unknown) => {
        console.error(e)
      })
    } else {
      this.scheduledEDDJob = window.setTimeout(() => {
        task().catch((e: unknown) => {
          console.error(e)
        })
      }, 1000)
    }
  }

  scheduleDynamicDataFetch = (skipExistCheck?: boolean, skipTagRefresh?: boolean) => {
    const MAX_QUEUE_SIZE = 50

    const task = async () => {
      this.scheduledJob = 0
      const productIds = this.productsQueueForDynamicTagFetch.filter(
        productId => skipExistCheck || !this.state.tagsVSProductId[productId],
      )
      this.productsQueueForDynamicTagFetch = []
      if (productIds.length) {
        if (!skipTagRefresh) {
          const productIdBatches = createBatches(productIds)
          const productDynamicDataRequests = productIdBatches.map(batch =>
            productService.getProductDynamicDataByProductIds(batch, true, this.zipCode),
          )
          const productDynamicDataResults = await Promise.allSettled(productDynamicDataRequests).then(filterFulfilled)
          productDynamicDataResults.forEach(productDynamicInfo => {
            this.state.tagsVSProductId = {
              ...this.state.tagsVSProductId,
              ...productDynamicInfo.productIdVsTags,
            }
            this.state.deliveryInfoVsProductId = {
              ...this.state.deliveryInfoVsProductId,
              ...productDynamicInfo.productIdVsDeliveryInfo,
            }
            this.state.newColorOrderVsProductId = {
              ...this.state.newColorOrderVsProductId,
              ...productDynamicInfo.newColorOrderVsProductId,
            }
            this.state.colorOrderVsProductId = {
              ...this.state.colorOrderVsProductId,
              ...productDynamicInfo.colorOrderVsProductId,
            }
            this.state.isWhiteGloveProductVsProductId = {
              ...this.state.isWhiteGloveProductVsProductId,
              ...productDynamicInfo.isWhiteGloveProductVsProductId,
            }
          })
        }
        this.publish()
      }
    }

    clearTimeout(this.scheduledJob)
    if (this.productsQueueForDynamicTagFetch.length >= MAX_QUEUE_SIZE) {
      task().catch((e: unknown) => {
        console.error(e)
      })
    } else {
      this.scheduledJob = window.setTimeout(() => {
        task().catch((e: unknown) => {
          console.error(e)
        })
      }, 1000)
    }
  }

  getProductTags = (productId: string | number) => {
    return this.state.tagsVSProductId[productId]
  }

  getProductDeliveryInfo = (productId: string | number) => {
    return this.state.deliveryInfoVsProductId[productId]
  }

  getDynamicProductColorOrder = (productId: string | number) => {
    return this.state.newColorOrderVsProductId[productId]
  }

  getProductColorOrder = (productId: string | number) => {
    return this.state.colorOrderVsProductId[productId]
  }

  getIsWhiteGloveProduct = (productId: string | number) => {
    return this.state.isWhiteGloveProductVsProductId[productId]
  }

  getProductEDD = (productId: string | number) => {
    return this.state.productEDDDetails[productId]
  }

  resolveDynamicData(productId: number | string, zipCode?: string, skipExistCheck?: boolean, skipTagRefresh?: boolean) {
    this.productsQueueForDynamicTagFetch.push(productId)
    this.zipCode = zipCode
    this.scheduleDynamicDataFetch(skipExistCheck, skipTagRefresh)
  }

  resolveEDDData(
    eddRequestedProducts: EDDRequestProductType,
    zipCode: string,
    wrapperRef: React.RefObject<any>,
    skipExistCheck?: boolean,
  ) {
    if (wrapperRef?.current && this.eddObserver) {
      if (this.zipCode !== zipCode) {
        this.eddObserver.disconnect()
        this.initialiseEDDIntersectionObserver()
        this.zipCode = zipCode
      }
      this.eddObserverDataMap.set(wrapperRef.current, { eddRequestedProducts, skipExistCheck })
      this.eddObserver.observe(wrapperRef.current)
    }
  }
}

const productDynamicDataStateManager = ProductDynamicDataStateManager.getInstance('productDynamicDataStateManager')

export default productDynamicDataStateManager
